import {
  ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, InjectionToken, OnInit, ViewChild
} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams} from 'ag-grid-community';
import {DateUtils} from '../../../common/utils/date';
import {SelectItem} from '../../../components/wfm-select/wfm-select.component';
import {EXTERNAL_FORECAST_NOT_AVAILABLE_KEY, showExternalForecastMessage} from './external-forecast-feature.guard';
import {ExternalForecastComparison, ExternalForecastErrorId, ExternalForecastYear} from './external-forecast.model';
import {ExternalForecastService} from './external-forecast.service';
import {
  averageOf, buildFiscalYear, formatCount, formatPercent, pickDefaultYear, toIntlLocale
} from './external-forecast.utils';

/** Source of "today" for the default-year rule; overridable in tests. */
export const EXTERNAL_FORECAST_CLOCK = new InjectionToken<() => Date>("EXTERNAL_FORECAST_CLOCK", {
  providedIn: "root",
  factory: () => () => new Date()
});

/** Month and total columns keep this width; spare grid width stays blank rather than being shared out. */
const MONTH_COL_WIDTH = 110;
const PINNED_ROW_COUNT = 2;
const ROW_HEADER_COL_ID = "rowHeader";
const TOTAL_COL_ID = "yearTotal";
const AVERAGE_COL_ID = "yearAverage";
const DARK_HEADER_CLASS = "ef-dark-header";
const SUMMARY_CELL_CLASS = "ef-summary-cell";
/** Parameter column width, as on Plan Detail. */
const PARAMETER_COL_WIDTH = 225;

type PageState = "loading" | "ready" | "error";
export type YearType = "calendar" | "fiscal";
export type ForecastMode = "combine" | "replace";

interface ComparisonRow {
  label: string;
  values: Record<string, number>;
  total: number;
  format: "count" | "percent";
  /** Drives the row colour (white vs grey) like Plan Detail; value cells are not editable until the BE exists. */
  editable: boolean;
  /** Emphasised calculated rows: bold, dark text like the period headers. */
  bold: boolean;
  /** Parameter label rendered bold in dark text (all bold rows plus the contacts-received and first % Distribution rows). */
  labelBold: boolean;
  /** First row of a major metric group: drawn with a thick divider above it. */
  groupStart: boolean;
  /** Set on an expandable group header row; its children carry the same value in parentKey. */
  groupKey?: string;
  parentKey?: string;
}

/** Mock CTs (BE not ready): their % distribution per month, summing to 100. */
const MOCK_CT_NAMES = ["CT 1", "CT 2", "CT 3", "CT 4", "CT 5"];
const MOCK_CT_DISTRIBUTION = [30, 25, 20, 15, 10];
const CT_DISTRIBUTION_GROUP = "ctDistribution";
const NEW_CT_CONTACTS_GROUP = "newCtContacts";

/** Parameter rows shown under the current-contacts row. Only the first has (mock) data until the BE is ready. */
const PARAMETER_ROWS: {labelKey: string; editable: boolean; bold: boolean; groupStart?: boolean}[] = [
  {labelKey: "external.forecast.param.pct.distribution", editable: false, bold: false},
  {labelKey: "external.forecast.param.business.metric", editable: true, bold: false},
  {labelKey: "external.forecast.param.pct.distribution", editable: true, bold: false},
  {labelKey: "external.forecast.param.contact.rate", editable: true, bold: false},
  {labelKey: "external.forecast.param.total.volume", editable: false, bold: true, groupStart: true},
  {labelKey: "external.forecast.param.pct.call.center.volume", editable: true, bold: false},
  {labelKey: "external.forecast.param.call.center.volume", editable: false, bold: true, groupStart: true},
  {labelKey: "external.forecast.param.ai.mix", editable: true, bold: false},
  {labelKey: "external.forecast.param.ai.volume", editable: true, bold: false},
  {labelKey: "external.forecast.param.human.volume", editable: false, bold: false, groupStart: true},
  {labelKey: "external.forecast.param.new.contacts.received", editable: false, bold: true, groupStart: true},
  {labelKey: "external.forecast.param.pct.distribution", editable: false, bold: true}
];

@Component({
  standalone: false,
  selector: 'esp-external-forecast',
  templateUrl: './external-forecast.component.html',
  styleUrl: './external-forecast.component.scss'
})
export class ExternalForecastComponent implements OnInit {
  @ViewChild("retryButton") retryButton: ElementRef<HTMLButtonElement>;

  planId: string;
  planName: string;
  state: PageState = "loading";
  /** Frozen for the life of the component (AC-16). Never refreshed; dropped on destroy. */
  snapshot: ExternalForecastComparison;
  selectedYear: ExternalForecastYear;
  yearItems: SelectItem[] = [];
  selectedYearItem: SelectItem;
  colDefs: ColDef[] = [];
  /** Rows that scroll: everything beneath the two pinned summary rows, minus the children of collapsed groups. */
  rowData: ComparisonRow[] = [];
  /** Current Contacts Received and the first % Distribution: fixed above the scrolling rows. */
  pinnedRows: ComparisonRow[] = [];
  yearAnnouncement = "";
  yearType: YearType = "calendar";
  /** No mode is selected until the user picks one. */
  forecastMode: ForecastMode | null = null;
  /** Fiscal year start month (1-12); only offered, and only applied, when the year type is fiscal. */
  fiscalStartMonth = 1;
  monthItems: SelectItem[] = [];
  selectedMonthItem: SelectItem;

  gridOptions: GridOptions = {
    defaultColDef: {
      editable: false,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      width: MONTH_COL_WIDTH
    },
    // Row colour follows Plan Detail: white for editable parameters and group headers, grey for non-editable ones.
    getRowClass: params => [params.data?.editable || params.data?.groupKey ? "editable-row" : "readonly-row", params.data?.bold ? "ef-bold-row" : "",
      params.data?.groupStart ? "ef-group-start" : ""],
    suppressClipboardPaste: true,
    suppressRowClickSelection: true,
    enableBrowserTooltips: true,
    // Group header rows span the whole row, like the Plan Detail MU group headers, instead of using the column grid.
    isFullWidthRow: params => !!params.rowNode.data?.groupKey,
    fullWidthCellRenderer: (params: ICellRendererParams) => this.groupHeaderRenderer(params),
    onGridReady: (event: GridReadyEvent) => this.gridApi = event.api
  };

  /** Group header rows currently collapsed; all groups start expanded. */
  private collapsedGroups = new Set<string>();
  private allRows: ComparisonRow[] = [];
  private gridApi: GridApi;
  private monthItemsLocale: string;
  private locale = toIntlLocale(null);
  /** date-fns locale for month names, following the app locale's language. */
  private dateLocale = DateUtils.getDatefnsLocale("en");
  private readonly destroyRef = inject(DestroyRef);
  private readonly clock = inject(EXTERNAL_FORECAST_CLOCK);

  constructor(private route: ActivatedRoute,
              private router: Router,
              private store: Store<any>,
              private translate: TranslateService,
              private modalService: NgbModal,
              private externalForecastService: ExternalForecastService,
              private host: ElementRef<HTMLElement>,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.planId = this.route.parent?.snapshot.paramMap.get("id") ?? this.route.snapshot.paramMap.get("id");

    this.store.select("state").pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state: any) => {
      this.planName = state?.currentPlanDetail?.planName;
      const locale = toIntlLocale(state?.locale);
      if (locale !== this.locale) {
        this.locale = locale;
        this.dateLocale = DateUtils.getDatefnsLocale(locale.split("-")[0]);
        this.render();
      }
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.render());

    this.load();
  }

  /** The single comparison GET; also used by Retry. */
  load() {
    this.state = "loading";
    this.externalForecastService.getExternalForecast(this.planId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: snapshot => this.onLoaded(snapshot),
        error: (err: HttpErrorResponse) => this.onLoadFailed(err)
      });
  }

  /**
   * Year changes only re-render from the snapshot: no HTTP call (AC-16).
   * wfm-select emits the selected SelectItem itself (ng-select has no bindValue there).
   */
  onYearSelected(selection: SelectItem | number) {
    const year = Number(typeof selection === "object" ? selection?.value : selection);
    const selected = this.snapshot?.years.find(y => y.year === year);
    if (!selected || selected === this.selectedYear) {
      return;
    }
    this.selectedYear = selected;
    this.selectedYearItem = this.yearItems.find(item => Number(item.value) === year);
    this.yearAnnouncement = String(year);
    this.render();
  }

  setYearType(yearType: YearType) {
    if (yearType === this.yearType) {
      return;
    }
    this.yearType = yearType;
    this.render();
  }

  /** The month a fiscal year starts in; like the year, a re-render from the snapshot only. */
  onMonthSelected(selection: SelectItem | number) {
    const month = Number(typeof selection === "object" ? selection?.value : selection);
    if (!month || month === this.fiscalStartMonth) {
      return;
    }
    this.fiscalStartMonth = month;
    this.selectedMonthItem = this.monthItems.find(item => Number(item.value) === month);
    this.render();
  }

  setForecastMode(forecastMode: ForecastMode) {
    this.forecastMode = forecastMode;
  }

  /** Exports exactly what the grid displays (collapsed groups stay collapsed), named after the current view. */
  exportCsv() {
    this.gridApi?.exportDataAsCsv({fileName: this.csvFileName()});
  }

  csvFileName(): string {
    const period = this.yearType === "fiscal"
      ? `${this.translate.instant("external.forecast.fiscal.year")} ${this.selectedYear.year} (${DateUtils.getMonthName(this.fiscalStartMonth, this.dateLocale)})`
      : `${this.translate.instant("external.forecast.calendar.year")} ${this.selectedYear.year}`;
    const mode = this.forecastMode
      ? this.translate.instant(this.forecastMode === "combine" ? "external.forecast.combine.forecast" : "external.forecast.replace.forecast")
      : null;
    return [this.planName, this.translate.instant("external.forecast.title"), period, mode].filter(Boolean).join(" - ");
  }

  private onLoaded(snapshot: ExternalForecastComparison) {
    this.snapshot = snapshot;
    this.yearItems = snapshot.years.map(y => ({label: String(y.year), value: y.year as any, object: y}));
    this.selectedYear = pickDefaultYear(snapshot.years, this.clock());
    this.selectedYearItem = this.yearItems.find(item => Number(item.value) === this.selectedYear.year);
    this.state = "ready";
    this.render();
  }

  private onLoadFailed(err: HttpErrorResponse) {
    const errorId = err?.error?.errorId;
    if (err?.status === 404 && errorId === ExternalForecastErrorId.DISABLED) {
      const backToPlan = () => this.router.navigate(["/site-plans-v5", this.planId]);
      showExternalForecastMessage(this.modalService, "external.forecast.title", EXTERNAL_FORECAST_NOT_AVAILABLE_KEY, "warn")
        .then(backToPlan, backToPlan);
      return;
    }
    if ((err?.status === 404 && errorId === ExternalForecastErrorId.PLAN_NOT_FOUND) || err?.status === 410) {
      const backToPlans = () => this.router.navigateByUrl("/plans");
      showExternalForecastMessage(this.modalService, "plan.notexist.title", "external.forecast.plan.gone", "error")
        .then(backToPlans, backToPlans);
      return;
    }
    if (err?.status === 401) {
      // Handled by the app's existing auth handling.
      return;
    }
    this.state = "error";
    this.cdr.detectChanges();
    this.retryButton?.nativeElement.focus();
  }

  private render() {
    if (!this.selectedYear) {
      return;
    }
    if (this.monthItemsLocale !== this.locale) {
      this.monthItemsLocale = this.locale;
      this.monthItems = Array.from({length: 12}, (_, i) => ({label: DateUtils.getMonthName(i + 1, this.dateLocale), value: i + 1 as any, object: null}));
      this.selectedMonthItem = this.monthItems[this.fiscalStartMonth - 1];
    }
    const year = this.yearType === "fiscal"
      ? buildFiscalYear(this.snapshot.years, this.selectedYear.year, this.fiscalStartMonth)
      : this.selectedYear;
    this.colDefs = this.buildColumnDefs(year);
    const contactsByMonth = Object.fromEntries(year.months.map(m => [m.month, m.contactsReceived]));
    // Mock: each month's share of the year's contacts; replaced by BE data later.
    const mockDistribution = Object.fromEntries(year.months.map(m =>
      [m.month, year.total ? m.contactsReceived / year.total * 100 : 0]));
    this.allRows = [
      {
        label: this.translate.instant("external.forecast.row.contacts.received"),
        values: contactsByMonth,
        total: year.total,
        format: "count",
        editable: false,
        bold: false,
        labelBold: true,
        groupStart: false
      },
      ...PARAMETER_ROWS.map((row, index): ComparisonRow => ({
        label: this.translate.instant(row.labelKey),
        values: index === 0 ? mockDistribution : {},
        total: index === 0 && year.total ? 100 : null,
        format: "percent",
        editable: row.editable,
        bold: row.bold,
        labelBold: row.bold || index === 0,
        groupStart: !!row.groupStart
      })),
      ...this.buildCtGroupRows(year)
    ];
    this.applyGroupState();
  }

  private row(label: string, partial: Partial<ComparisonRow>): ComparisonRow {
    return {
      label, values: {}, total: null, format: "percent", editable: false, bold: false, labelBold: false,
      groupStart: false, ...partial
    };
  }

  /** The two expandable CT groups: editable mock % CT Distribution and calculated New CT Contacts Received. */
  private buildCtGroupRows(year: ExternalForecastYear): ComparisonRow[] {
    const months = year.months.map(m => m.month);
    const perMonth = (value: number) => Object.fromEntries(months.map(m => [m, value]));
    return [
      this.row(this.translate.instant("external.forecast.param.ct.distribution"),
        {bold: true, labelBold: true, groupStart: true, groupKey: CT_DISTRIBUTION_GROUP}),
      ...MOCK_CT_NAMES.map((name, i) => this.row(name,
        {values: perMonth(MOCK_CT_DISTRIBUTION[i]), editable: true, parentKey: CT_DISTRIBUTION_GROUP})),

      this.row(this.translate.instant("external.forecast.param.new.ct.contacts.received"),
        {bold: true, labelBold: true, groupStart: true, groupKey: NEW_CT_CONTACTS_GROUP}),
      // Values are calculated in a later phase.
      ...MOCK_CT_NAMES.map(name => this.row(name, {format: "count", parentKey: NEW_CT_CONTACTS_GROUP}))
    ];
  }

  /** Pinned and scrolling rows together, as displayed top to bottom. */
  get displayedRows(): ComparisonRow[] {
    return [...this.pinnedRows, ...this.rowData];
  }

  private applyGroupState() {
    this.pinnedRows = this.allRows.slice(0, PINNED_ROW_COUNT);
    this.rowData = this.allRows.slice(PINNED_ROW_COUNT).filter(r => !r.parentKey || !this.collapsedGroups.has(r.parentKey));
  }

  private toggleGroup(groupKey: string, params: ICellRendererParams) {
    if (!this.collapsedGroups.delete(groupKey)) {
      this.collapsedGroups.add(groupKey);
    }
    this.applyGroupState();
    params.api.redrawRows({rowNodes: [params.node]});
  }

  /** Full-width group header: same expand/collapse affordance and layout as the Plan Detail MU rollup header. */
  private groupHeaderRenderer(params: ICellRendererParams): HTMLElement {
    const groupKey: string = params.data.groupKey;
    const collapsed = this.collapsedGroups.has(groupKey);
    const wrapper = document.createElement("div");
    wrapper.className = "ef-group-header";
    wrapper.tabIndex = 0;
    wrapper.setAttribute("role", "button");
    wrapper.setAttribute("aria-expanded", String(!collapsed));
    wrapper.innerHTML = `<span class="ag-group-${collapsed ? "contracted" : "expanded"}">` +
      `<span class="ag-icon ag-icon-tree-${collapsed ? "closed" : "open"}"></span></span>`;
    const text = document.createElement("span");
    text.className = "ef-group-label";
    text.textContent = params.data.label ?? "";
    text.title = params.data.label ?? "";
    wrapper.appendChild(text);
    wrapper.addEventListener("click", () => this.toggleGroup(groupKey, params));
    wrapper.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleGroup(groupKey, params);
      }
    });
    return wrapper;
  }

  private buildColumnDefs(year: ExternalForecastYear): ColDef[] {
    const format = (value: number, row?: ComparisonRow) => value === null || value === undefined ? ""
      : row?.format === "percent" ? formatPercent(value, this.locale) : formatCount(value, this.locale);
    // Like Plan Detail, headers carry tooltips; only the Parameter cells do (value cells have none).
    const parameterHeader = this.translate.instant("plan.label.param");
    const totalHeader = this.translate.instant("plan.label.total");
    const averageHeader = this.translate.instant("plan.label.average");
    return [
      {
        colId: ROW_HEADER_COL_ID,
        headerName: parameterHeader,
        headerTooltip: parameterHeader,
        field: "label",
        tooltipField: "label",
        pinned: "left",
        width: PARAMETER_COL_WIDTH,
        suppressSizeToFit: true,
        cellClass: params => params.data?.labelBold ? "ef-row-header ef-label-bold" : "ef-row-header",
        // Inline so no global grid rule can override the emphasis (same dark text as the period headers).
        cellStyle: params => ({
          ...(params.data?.labelBold ? {fontWeight: "bold", color: "rgb(46, 46, 46)"} : {}),
          ...(params.data?.parentKey ? {paddingLeft: "22px"} : {})
        })
      },
      ...year.months.map((m): ColDef => {
        const header = DateUtils.getMonthYear(`${m.month}-01`, this.dateLocale);
        return {
          colId: m.month,
          headerName: header,
          headerTooltip: header,
          headerClass: DARK_HEADER_CLASS,
          valueGetter: params => params.data?.values[m.month],
          valueFormatter: params => format(params.value, params.data),
          type: "rightAligned"
        };
      }),
      {
        colId: TOTAL_COL_ID,
        headerName: totalHeader,
        headerTooltip: totalHeader,
        headerClass: DARK_HEADER_CLASS,
        cellClass: SUMMARY_CELL_CLASS,
        field: "total",
        // Anchored to the grid's right edge; spare width stays blank between the last month and Total.
        pinned: "right",
        valueFormatter: params => format(params.value, params.data),
        type: "rightAligned"
      },
      {
        colId: AVERAGE_COL_ID,
        headerName: averageHeader,
        headerTooltip: averageHeader,
        headerClass: DARK_HEADER_CLASS,
        // Pinned right after Total so both stay visible while the months scroll.
        pinned: "right",
        cellClass: SUMMARY_CELL_CLASS,
        valueGetter: params => averageOf(Object.values(params.data?.values ?? {})),
        valueFormatter: params => format(params.value, params.data),
        type: "rightAligned"
      }
    ];
  }
}
